import { useEffect, useMemo } from 'react'
import { Material, Object3D, Raycaster, Vector2 } from 'three'
import { useThree } from '@react-three/fiber'
import useSceneElementDebugStore, { DebugHit } from './store'

// Treat a pointer up as a "click" only if it barely moved since pointer down,
// so camera drags (orbit/pan) don't trigger an inspection. Mirrors the
// threshold used by the scene context-menu trigger.
const CLICK_MOVE_THRESHOLD_PX = 6

// Walk an object and its ancestors for the first meaningful identifier so a
// generated, unnamed mesh still shows something useful (its parent's name or a
// userData id) rather than just "Mesh".
const resolveLabel = (object: Object3D): string => {
  let current: Object3D | null = object
  while (current) {
    if (current.name) return current.name
    const userData = current.userData ?? {}
    const candidate = userData.elementId ?? userData.guid ?? userData.id ?? userData.type
    if (typeof candidate === 'string' && candidate) return candidate
    current = current.parent
  }
  return object.type
}

// An object is only really visible if it and every ancestor is visible – a
// hidden parent group hides its children even though the child's own `visible`
// is still true.
const isEffectivelyVisible = (object: Object3D): boolean => {
  let current: Object3D | null = object
  while (current) {
    if (!current.visible) return false
    current = current.parent
  }
  return true
}

const readMaterial = (
  object: Object3D,
): { opacity: number | null; transparent: boolean | null } => {
  const material = (object as unknown as { material?: Material | Material[] }).material
  const single = Array.isArray(material) ? material[0] : material
  if (!single) return { opacity: null, transparent: null }
  return { opacity: single.opacity ?? null, transparent: single.transparent ?? null }
}

/**
 * Dev-only, scene-level click inspector. While mounted, a click anywhere in the
 * scene raycasts against *every* object (not just those with R3F handlers) and
 * publishes the full front-to-back stack of hits to the debug store, so the
 * overlay box can show what sits on top of a clicked spot – the tool for
 * confirming "something invisible lays on top of the comment".
 *
 * Observe-only: it doesn't stop propagation, so normal scene interaction keeps
 * working alongside it.
 */
const SceneElementDebugController = (): null => {
  const gl = useThree(state => state.gl)
  const camera = useThree(state => state.camera)
  const scene = useThree(state => state.scene)

  const setResult = useSceneElementDebugStore(state => state.setResult)
  const clear = useSceneElementDebugStore(state => state.clear)

  const raycaster = useMemo(() => new Raycaster(), [])

  useEffect(() => {
    const canvas = gl.domElement
    const down = new Vector2()

    const handlePointerDown = (event: PointerEvent) => {
      down.set(event.clientX, event.clientY)
    }

    const handlePointerUp = (event: PointerEvent) => {
      const movedPx = Math.hypot(event.clientX - down.x, event.clientY - down.y)
      if (movedPx > CLICK_MOVE_THRESHOLD_PX) return

      const rect = canvas.getBoundingClientRect()
      const ndc = new Vector2(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1,
      )

      raycaster.setFromCamera(ndc, camera)
      const intersections = raycaster.intersectObjects(scene.children, true)

      const seen = new Set<string>()
      const hits: DebugHit[] = []
      for (const intersection of intersections) {
        const { object } = intersection
        if (seen.has(object.uuid)) continue
        seen.add(object.uuid)

        const { opacity, transparent } = readMaterial(object)
        hits.push({
          label: resolveLabel(object),
          name: object.name,
          type: object.type,
          uuid: object.uuid.slice(0, 8),
          distance: intersection.distance,
          visible: isEffectivelyVisible(object),
          point: [intersection.point.x, intersection.point.y, intersection.point.z],
          opacity,
          transparent,
          userData: object.userData ?? {},
        })
      }

      setResult(hits.length ? hits[0].point : null, hits)
    }

    canvas.addEventListener('pointerdown', handlePointerDown)
    canvas.addEventListener('pointerup', handlePointerUp)
    return () => {
      canvas.removeEventListener('pointerdown', handlePointerDown)
      canvas.removeEventListener('pointerup', handlePointerUp)
    }
  }, [gl, camera, scene, raycaster, setResult])

  // Clear the last result when the tool is switched off (component unmounts).
  useEffect(() => clear, [clear])

  return null
}

export default SceneElementDebugController
